/**
 * Shared water look (Layer 1 of the water brief) — paradigm-independent, so the
 * ocean patch, and later lakes and rivers, all read as the SAME substance. The
 * geometry/animation differs per body (a projected Gerstner grid here; flat
 * discs / flow-mapped ribbons later); this module owns only how the surface is
 * SHADED once a fragment knows its world position, normal, water depth, wave
 * crest height and rim fade.
 *
 * Effects, in the brief's priority order:
 *   1. Depth-based colour absorption — the keystone. Bright shallow → dark
 *      saturated deep, by metres of water over the floor. This is the cue the
 *      previous (reverted) attempt lacked entirely.
 *   2. Fresnel — reflective at grazing angles, the body's own colour looking
 *      straight down.
 *   3. Shoreline / contact foam — driven by depth → 0, NOT a noise texture.
 *   4. Reflection — analytic sky gradient + sun glint (cheapest; an env cubemap
 *      can replace `uSkyColor` later via the documented hook).
 *   5. Refraction — stylised: a faint normal-driven wobble of the deep tint. A
 *      true scene-refraction sample is deferred (would need a render-target
 *      pre-pass; out of scope for v1 per the locked decision).
 */
import * as THREE from "three";
import { ENVIRONMENT } from "../../visual/styleProfiles";

export interface WaterColors {
  shallow: THREE.Vector3;
  deep: THREE.Vector3;
  foam: THREE.Vector3;
}

/**
 * Derive a shallow/deep/foam triad from a body's base colour so each planet's
 * sea keeps its hue: deep is the base darkened + pushed bluer (light is
 * absorbed red-first with depth); shallow is brightened toward a pale tint.
 */
export function deriveWaterColors(base: [number, number, number]): WaterColors {
  const [r, g, b] = base;
  const deep = new THREE.Vector3(r * 0.32, g * 0.42, b * 0.58);
  // Shallow is lighter than deep but NOT a bright pale-blue band — the old additive
  // brightening (+0.22/+0.24) lit the whole shoreline shallows into a glaring halo.
  const shallow = new THREE.Vector3(
    Math.min(1, r * 0.9 + 0.04),
    Math.min(1, g * 0.92 + 0.1),
    Math.min(1, b * 0.95 + 0.12),
  );
  const foam = new THREE.Vector3(0.92, 0.96, 1.0);
  return { shallow, deep, foam };
}

/**
 * Uniforms every water surface shares. `seaColor` seeds the colour triad;
 * everything else is a tunable knob with a sensible stylised default. Callers
 * merge their own geometry uniforms (centre, basis, time…) on top.
 */
export function sharedWaterUniforms(seaColor: [number, number, number]): {
  [k: string]: THREE.IUniform;
} {
  const c = deriveWaterColors(seaColor);
  return {
    uShallow: { value: c.shallow },
    uDeep: { value: c.deep },
    uFoam: { value: c.foam },
    /** Metres of water at which the colour is essentially fully "deep". */
    uMaxDepth: { value: 18.0 },
    /** Absorption rate — higher darkens faster with depth (tighter shallow band). */
    uAbsorb: { value: 0.32 },
    /** Strength of the sky reflection mixed in at grazing angles. */
    uReflStr: { value: 0.88 },
    /** Sun-glint specular exponent — broad-ish so grazing sun reads as a glitter
     *  PATH, but tight enough that it doesn't pool into a bright "glowing ground". */
    uShine: { value: 95.0 },
    /** Sun-glint intensity (toned down — the full-day glint was reading as a too-bright
     *  light-blue patch on the water). */
    uGlint: { value: 0.26 },
    // Live shared presentation scalar, including already-created river/ocean materials.
    uArtGlint: { get value() { return ENVIRONMENT.glint; } },
    /** Metres of depth over which shoreline foam fades out (kept narrow). */
    uFoamWidth: { value: 0.65 },
    /** How white the waterline gets at its brightest. Live knob alongside uFoamWidth
     *  — 0 removes the surf entirely. */
    uFoamStr: { value: 0.16 },
    /** Wave-crest amplitude scale (also used by the patch geometry). */
    uAmp: { value: 1.0 },
    /** Fallback sky colour when fog is unavailable (env-cubemap hook later). */
    uSkyColor: { value: new THREE.Vector3(0.5, 0.7, 1.0) },
    uSunDir: { value: new THREE.Vector3(0, 1, 0) },
    uSunColor: { value: new THREE.Vector3(1, 1, 1) },
    /** Daylight 0..1 (this side's day factor) — the unlit surface darkens with it. */
    uDayLight: { value: 1.0 },
  };
}

/** GLSL declarations for the shared uniforms above — included by each shader. */
export const WATER_UNIFORMS_GLSL = /* glsl */ `
uniform vec3 uShallow;
uniform vec3 uDeep;
uniform vec3 uFoam;
uniform float uMaxDepth;
uniform float uAbsorb;
uniform float uReflStr;
uniform float uShine;
uniform float uGlint;
uniform float uArtGlint;
uniform float uFoamWidth;
uniform float uFoamStr;
uniform float uAmp;
uniform vec3 uSkyColor;
uniform vec3 uSunDir;
uniform vec3 uSunColor;
uniform float uDayLight;
`;

/**
 * The shared shading function. A body's fragment shader computes its world pos,
 * normal, water depth (metres), wave crest height and rim fade, then calls this
 * to get the final premultiplied-ish colour + alpha. Pure function of its args
 * + the shared uniforms; no geometry assumptions leak in.
 *
 *   depth   — metres of water over the floor (0 at the shoreline).
 *   edge    — patch rim fade in [0,1] (1 = interior, 0 = dissolve to far sea).
 *   sky     — reflected sky colour (fogColor under USE_FOG, else uSkyColor).
 *   reflK   — scales how MUCH of `sky` the Fresnel mixes in (1 = normal). Use it
 *             where the surface should stop mirroring: dialling this down shows the
 *             water's own colour, whereas leaving reflection at full strength and
 *             feeding it a fallback `sky` just paints the fog colour on the water.
 */
export const WATER_SHADE_GLSL = /* glsl */ `
vec4 waterShadeSurface(vec3 worldPos, vec3 N, vec3 V, float depth, float edge, vec3 sky, float reflK, float shadow, float roughness, float foamCoverage) {
  // shadow: 1 = full sun, <1 where a cloud sits between this water and the sun. Dims the
  // SUN-driven cues (diffuse body, glint, foam) but NOT the geometric sky reflection.
  // The surface is unlit (it computes its own colour), so unlike the lit far
  // sphere it would NOT darken at night — leaving the near water glowing pale.
  // Tie its diffuse + foam to this side's daylight (with a soft moonlit floor).
  float lit = 0.16 + 0.84 * uDayLight;

  // 1. Depth-based colour absorption — the dominant "reads as water" cue.
  float dAbs = 1.0 - exp(-max(depth, 0.0) * uAbsorb);
  float dLin = clamp(depth / uMaxDepth, 0.0, 1.0);
  vec3 body = mix(uShallow, uDeep, max(dAbs, dLin));

  // 5. Stylised refraction: a faint deep-tint wobble driven by the surface
  //    normal (no scene sample). Reads as shimmer over the floor in the shallows.
  body += uDeep * (N.x + N.y) * 0.04 * (1.0 - dLin);
  body *= lit * shadow; // diffuse water darkens with the night + under cloud shadow

  float ndv = clamp(dot(N, V), 0.0, 1.0);

  // 2. Fresnel + 4. sky reflection: mirror-like at grazing angles. The sky arg may
  //    carry a real planar reflection (terrain/players/moons/sun), which already
  //    encodes day/night correctly — so only a light night trim (was a harsh x0.45
  //    that erased reflected celestial bodies on the night sea).
  float fres = pow(1.0 - ndv, 4.0);
  // Reflection strength dips at DUSK and stays high at true-night + day: at dusk the
  // sky has a bright pale-blue TWILIGHT BAND that the grazing water otherwise mirrors
  // into a glaring light-blue glow; at true night the sky is dark (only moons/stars to
  // mirror) and in day terrain reflects. U-shaped in daylight to thread that needle.
  float reflDay = 0.85 - 1.7 * uDayLight + 1.7 * uDayLight * uDayLight; // 0.85 at night/day, ~0.43 at dusk
  float refl = uReflStr * reflDay * reflK * mix(1.12,0.88,roughness);
  vec3 col = mix(body, sky, fres * refl);

  // 4. Sun glint off the wave normals — the strongest "alive" cue (sun colour is
  //    already daylight-dimmed, so the glint fades out at night on its own).
  vec3 H = normalize(uSunDir + V);
  float spec = pow(max(dot(N, H), 0.0), uShine * mix(1.6,0.4,roughness));
  col += uSunColor * min(spec * uGlint * uArtGlint, 0.32) * shadow; // glint dies where a cloud blocks the sun

  // 3. Shoreline foam where depth → 0 — the ONLY white the water paints on itself.
  //    Crest foam is deliberately gone: it fired on the rare frames when all seven
  //    swells crested in phase, which reads as surf up close but, past the distance
  //    where a pixel spans several wavelengths, is just a point sample of noise —
  //    a field of white specks boiling every frame across the far sea. The waves
  //    are far too calm to owe anything to whitecaps, so white lives at the
  //    waterline and nowhere else.
  float shore = 1.0 - smoothstep(0.0, uFoamWidth, depth);
  float foam = clamp(shore * shore * uFoamStr * foamCoverage,0.0,0.65);
  // Foam is a DAYTIME read — surf catches sunlight. On the night side the 'lit'
  // floor (0.16) was enough to keep a pale waterline band, which from orbit traces
  // every coast as a glowing cyan outline. Fade foam out with the daylight on this
  // side so the dark hemisphere has no rim (alpha follows, so it stops forcing opacity).
  foam *= smoothstep(0.0, 0.3, uDayLight);
  col = mix(col, uFoam * lit * shadow, foam);

  // Alpha: the interior is essentially opaque so the far sea sphere never ghosts
  // through it (depth COLOUR, not see-through, carries the depth read); only the
  // rim fades, dissolving into the matching far sphere.
  float alpha = mix(0.97, 1.0, max(fres, dLin));
  alpha = max(alpha, foam);
  alpha *= edge;
  return vec4(col, alpha);
}
// Existing distant water keeps its original response. Nearby surfaces supply
// their own roughness and broken contact pattern without a new material pass.
vec4 waterShade(vec3 worldPos, vec3 N, vec3 V, float depth, float edge, vec3 sky, float reflK, float shadow) {
  return waterShadeSurface(worldPos,N,V,depth,edge,sky,reflK,shadow,0.5,1.0);
}
`;
